import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { conversationApi } from '@formosa/shared/api/im';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import { asId } from './travelChatModel';
import {
  DIRECT_CONVERSATION_ACTIONS,
  openDirectConversation,
  parseDirectCapability,
} from './openDirectConversation';
import { directConversationErrorMessage } from './travelDirectError';

export default function DirectMessageButton({ peerMemberId, label, className }) {
  const { isAuthenticated } = useAuth();
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [capability, setCapability] = useState(null);
  const generation = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      generation.current += 1;
    };
  }, [peerMemberId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setCapability(null);
      return undefined;
    }
    const peer = asId(peerMemberId);
    if (!peer || typeof conversationApi.getDirectCapability !== 'function') {
      setCapability(null);
      return undefined;
    }
    const requestGeneration = ++generation.current;
    let cancelled = false;
    setCapability(null);
    conversationApi.getDirectCapability(peer)
      .then((raw) => {
        if (cancelled || requestGeneration !== generation.current || !alive.current) return;
        setCapability(parseDirectCapability(raw));
      })
      .catch(() => {
        if (cancelled || requestGeneration !== generation.current || !alive.current) return;
        setCapability(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, peerMemberId]);

  async function onClick() {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: authReturnLocation(location) } });
      return;
    }
    if (busy) return;
    const requestGeneration = ++generation.current;
    setBusy(true);
    setError(null);
    try {
      await openDirectConversation(peerMemberId, {
        navigate,
        capability,
        isCurrent: () => alive.current && requestGeneration === generation.current,
      });
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setError(cause);
    } finally {
      if (requestGeneration === generation.current) setBusy(false);
    }
  }

  if (isAuthenticated && capability?.action === DIRECT_CONVERSATION_ACTIONS.NONE) {
    return null;
  }

  return (
    <span className="travel-dm">
      <button type="button" className={className} disabled={busy} onClick={onClick} aria-busy={busy}>
        {busy ? '開啟中…' : label}
      </button>
      {error ? (
        <p className="travel-dm__error" role="alert">{directConversationErrorMessage(error, t)}</p>
      ) : null}
    </span>
  );
}
